if (!customElements.get('weekly-preorder-countdown')) {
  class WeeklyPreorderCountdown extends HTMLElement {
    constructor() {
      super();
      this.timeZone = this.dataset.timezone || 'Europe/Madrid';
      this.locale = this.dataset.locale || document.documentElement.lang || 'en';
      this.tick = this.tick.bind(this);
    }

    connectedCallback() {
      this.messageEl = this.querySelector('.js-weekly-preorder-countdown-message');
      this.shippingDateEl = this.querySelector('.js-weekly-preorder-shipping-date');
      this.windowTextEl = this.querySelector('.js-weekly-preorder-window-text');
      this.separatorEl = this.querySelector('.weekly-preorder-countdown__separator');
      this.shippingEl = this.querySelector('.weekly-preorder-countdown__shipping');
      this.cancelEl = this.querySelector('.js-weekly-preorder-cancel-text');

      this.update();
      this.start();
    }

    disconnectedCallback() {
      clearTimeout(this.timeout);
      clearInterval(this.interval);
    }

    start() {
      const now = Date.now();
      const msUntilNextMinute = (60 - new Date(now).getSeconds()) * 1000 + 50;
      this.timeout = setTimeout(() => {
        this.tick();
        this.interval = setInterval(this.tick, 60000);
      }, msUntilNextMinute);
    }

    tick() {
      this.update();
    }

    update() {
      const schedule = this.getSchedule(new Date());

      // Mirror the estimated shipping date into the Stock/Pre-order toggle's
      // Pre-order row. Done before the early-return so it stays populated even
      // when the banner itself is hidden (e.g. Stock selected).
      this.updateAvailabilityShipping(schedule);

      // Central override (theme settings -> Weekly shipping countdown). While it is
      // on, the banner stays visible even outside a live window, so every surface
      // shows the same unified information. Blank fields fall through to the
      // calculated value, so a partial override is valid.
      const override = this.overrideActive();

      if (!override && (!schedule.isActive || schedule.remainingMinutes < 0)) {
        this.hidden = true;
        return;
      }

      this.hidden = false;
      if (this.windowTextEl) {
        this.windowTextEl.textContent =
          this.overrideWindowLabel() || this.buildWindowLabel(schedule.startDate);
      }
      if (this.messageEl) {
        const msg = this.overrideMessage();
        if (msg) {
          this.messageEl.textContent = msg;
        } else if (schedule.isActive && schedule.remainingMinutes >= 0) {
          this.messageEl.textContent = this.buildCountdownText(schedule.remainingMinutes);
        } else {
          this.messageEl.textContent = '';
        }
        this.messageEl.hidden = !this.messageEl.textContent;
      }
      // Guarded like every other element above: this was the one unguarded
      // access, and a consumer without a shipping-date element would throw
      // here and abort the whole update, leaving the banner blank.
      var shippingText = '';
      if (this.shippingDateEl) {
        // A merchant override (theme settings -> Stock / Pre-order toggle) wins over
        // the calculated weekly date, here and in updateAvailabilityShipping below.
        shippingText = this.shippingDateOverride() || this.formatDate(schedule.shippingDate);
        this.shippingDateEl.textContent = shippingText;
      }
      if (this.cancelEl) this.cancelEl.hidden = false;

      if (this.separatorEl) this.separatorEl.hidden = !shippingText;
      if (this.shippingEl) this.shippingEl.hidden = !shippingText;
    }

    updateAvailabilityShipping(schedule) {
      const scope = this.closest('.shopify-section, .js-quickbuy, .cc-featured-product') || document;
      const els = scope.querySelectorAll('.js-availability-shipping-date');
      if (!els.length) return;
      const text = this.shippingDateOverride() || this.formatDate(schedule.shippingDate);
      els.forEach((el) => { el.textContent = text; });
    }

    shippingDateOverride() {
      return (this.dataset.shippingDateOverride || '').trim();
    }

    overrideActive() {
      return (this.dataset.overrideActive || '').trim() === 'true';
    }

    // ---- schedule parameters, all from theme settings via data attributes ----
    // Fallbacks match the values these were hardcoded to before the rebuild, so a
    // surface that somehow renders without the data snippet still behaves sanely.
    closeWeekday() {
      const n = Number.parseInt(this.dataset.closeWeekday, 10);
      return Number.isFinite(n) && n >= 0 && n <= 6 ? n : 0; // 0 = Sunday
    }

    parseClock(raw, fallbackH, fallbackM) {
      const m = /^(\d{1,2}):(\d{2})$/.exec((raw || '').trim());
      if (!m) return { hour: fallbackH, minute: fallbackM };
      const h = Number.parseInt(m[1], 10);
      const mi = Number.parseInt(m[2], 10);
      if (!Number.isFinite(h) || h < 0 || h > 23 || !Number.isFinite(mi) || mi < 0 || mi > 59) {
        return { hour: fallbackH, minute: fallbackM };
      }
      return { hour: h, minute: mi };
    }

    closeClock() { return this.parseClock(this.dataset.closeTime, 23, 59); }
    openClock() { return this.parseClock(this.dataset.openTime, 12, 0); }

    // Launch moment = the configured date at the window's opening time, in the
    // store timezone. Blank or past -> normal weekly behaviour.
    launchUtcMs() {
      const raw = (this.dataset.countdownUntil || '').trim();
      const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
      if (!m) return null;
      const openAt = this.openClock();
      return this.zonedTimeToUtc(
        Number.parseInt(m[1], 10),
        Number.parseInt(m[2], 10),
        Number.parseInt(m[3], 10),
        openAt.hour,
        openAt.minute,
        0
      );
    }

    shipLeadDays() {
      const n = Number.parseInt(this.dataset.shipLeadDays, 10);
      return Number.isFinite(n) && n >= 0 ? n : 12;
    }

    overrideWindowLabel() {
      return (this.dataset.overrideWindowLabel || '').trim();
    }

    overrideMessage() {
      return (this.dataset.overrideMessage || '').trim();
    }

    getSchedule(now) {
      const nowParts = this.getZonedParts(now);
      const weekday = this.getWeekday(nowParts);

      // Schedule shape comes entirely from theme settings (data attributes).
      const closeWeekday = this.closeWeekday();      // 0 = Sunday .. 6 = Saturday
      const closeAt = this.closeClock();             // window shuts
      const openAt = this.openClock();               // window opens, day after close
      const leadDays = this.shipLeadDays();

      // The cycle opens the day after it closes. Pause for the first minute of that
      // day so a rolled-over window never renders a stale "0 minutes left".
      const openWeekday = (closeWeekday + 1) % 7;
      const isRolloverPause =
        weekday === openWeekday && nowParts.hour === 0 && nowParts.minute < 1;

      // Days back to the open day, and forward to the close day, from today.
      const daysSinceOpen = (weekday - openWeekday + 7) % 7;
      const daysUntilClose = (closeWeekday - weekday + 7) % 7;

      const startLocal = new Date(Date.UTC(
        nowParts.year,
        nowParts.month - 1,
        nowParts.day - daysSinceOpen,
        openAt.hour,
        openAt.minute,
        0
      ));

      const endLocal = new Date(Date.UTC(
        nowParts.year,
        nowParts.month - 1,
        nowParts.day + daysUntilClose,
        closeAt.hour,
        closeAt.minute,
        0
      ));

      const startUtcMs = this.zonedTimeToUtc(
        startLocal.getUTCFullYear(),
        startLocal.getUTCMonth() + 1,
        startLocal.getUTCDate(),
        openAt.hour,
        openAt.minute,
        0
      );

      const endUtcMs = this.zonedTimeToUtc(
        endLocal.getUTCFullYear(),
        endLocal.getUTCMonth() + 1,
        endLocal.getUTCDate(),
        closeAt.hour,
        closeAt.minute,
        0
      );

      const endDisplayUtcMs = this.zonedTimeToUtc(
        endLocal.getUTCFullYear(),
        endLocal.getUTCMonth() + 1,
        endLocal.getUTCDate(),
        openAt.hour,
        openAt.minute,
        0
      );

      const shippingLocal = new Date(Date.UTC(
        endLocal.getUTCFullYear(),
        endLocal.getUTCMonth(),
        endLocal.getUTCDate() + leadDays,
        openAt.hour,
        openAt.minute,
        0
      ));

      const shippingUtcMs = this.zonedTimeToUtc(
        shippingLocal.getUTCFullYear(),
        shippingLocal.getUTCMonth() + 1,
        shippingLocal.getUTCDate(),
        openAt.hour,
        openAt.minute,
        0
      );

      const result = {
        isActive: !isRolloverPause,
        remainingMinutes: Math.floor((endUtcMs - now.getTime()) / 60000),
        startDate: new Date(startUtcMs),
        endDate: new Date(endDisplayUtcMs),
        shippingDate: new Date(shippingUtcMs)
      };

      // Pre-launch: until the weekly cycle actually begins, the timer counts down
      // to that launch moment instead of to this week's close. Everything else -
      // window label, shipping date - is left exactly as calculated.
      const launchMs = this.launchUtcMs();
      if (launchMs && launchMs > now.getTime()) {
        result.isActive = true;
        result.remainingMinutes = Math.floor((launchMs - now.getTime()) / 60000);
      }

      return result;
    }

    getZonedParts(date) {
      if (!this.partsFormatter) {
        this.partsFormatter = new Intl.DateTimeFormat('en-GB', {
          timeZone: this.timeZone,
          weekday: 'short',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hourCycle: 'h23'
        });
      }

      const values = {};
      this.partsFormatter.formatToParts(date).forEach((part) => {
        values[part.type] = part.value;
      });

      return {
        weekday: values.weekday,
        year: Number(values.year),
        month: Number(values.month),
        day: Number(values.day),
        hour: Number(values.hour),
        minute: Number(values.minute),
        second: Number(values.second)
      };
    }

    getWeekday(parts) {
      const fakeDate = new Date(Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second));
      const weekday = fakeDate.getUTCDay();
      return weekday === 0 ? 7 : weekday;
    }

    zonedTimeToUtc(year, month, day, hour, minute, second) {
      const target = Date.UTC(year, month - 1, day, hour, minute, second);
      let utcGuess = target;

      for (let i = 0; i < 3; i += 1) {
        const guessParts = this.getZonedParts(new Date(utcGuess));
        const guessAsUtc = Date.UTC(
          guessParts.year,
          guessParts.month - 1,
          guessParts.day,
          guessParts.hour,
          guessParts.minute,
          guessParts.second
        );
        utcGuess += target - guessAsUtc;
      }

      return utcGuess;
    }

    buildCountdownText(totalMinutes) {
      const labels = {
        dayOne: this.dataset.labelDayOne || 'day',
        dayOther: this.dataset.labelDayOther || 'days',
        hourShort: this.dataset.labelHourShort || 'h',
        minuteShort: this.dataset.labelMinuteShort || 'min'
      };

      const days = Math.floor(totalMinutes / 1440);
      const hours = Math.floor((totalMinutes % 1440) / 60);
      const minutes = Math.max(totalMinutes % 60, 0);
      const parts = [];

      if (days > 0) {
        parts.push(`${days} ${days === 1 ? labels.dayOne : labels.dayOther}`);
        parts.push(`${hours}${labels.hourShort}`);
        parts.push(`${minutes} ${labels.minuteShort}`);
      } else if (hours > 0 || totalMinutes > 0) {
        parts.push(`${hours}${labels.hourShort}`);
        parts.push(`${minutes} ${labels.minuteShort}`);
      } else {
        parts.push(`0${labels.hourShort}`);
        parts.push(`0 ${labels.minuteShort}`);
      }

      return parts.join(' · ');
    }

    buildWindowLabel(startDate) {
      const label = this.dataset.windowLabel !== undefined ? this.dataset.windowLabel : 'Pre-order';
      const cycle = this.getCycleNumber(startDate);
      return `${label} #${cycle}`;
    }

    buildWindowText(startDate, remainingMinutes) {
      const label = this.dataset.windowLabel !== undefined ? this.dataset.windowLabel : 'Pre-order';
      const cycle = this.getCycleNumber(startDate);
      const countdown = this.buildCountdownText(remainingMinutes);
      return `${label} #${cycle} — ${countdown}`;
    }

    getCycleNumber(startDate) {
      const baseNumber = Number.parseInt(this.dataset.cycleReferenceNumber || '1', 10) || 1;
      const referenceDate = this.getReferenceStartDate(startDate);
      const dayDiff = this.getCalendarDayIndex(startDate) - this.getCalendarDayIndex(referenceDate);
      const weekDiff = Math.floor(dayDiff / 7);
      return Math.max(1, baseNumber + weekDiff);
    }

    getReferenceStartDate(fallbackDate) {
      const raw = (this.dataset.cycleReferenceDate || '').trim();
      const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);

      if (!match) return fallbackDate;

      const [, year, month, day] = match;
      const utcMs = this.zonedTimeToUtc(Number(year), Number(month), Number(day), 12, 0, 0);
      return new Date(utcMs);
    }

    getCalendarDayIndex(date) {
      const parts = this.getZonedParts(date);
      return Math.floor(Date.UTC(parts.year, parts.month - 1, parts.day) / 86400000);
    }

    formatDate(date) {
      if (!this.dateFormatter) {
        this.dateFormatter = new Intl.DateTimeFormat(this.locale, {
          timeZone: this.timeZone,
          month: 'long',
          day: 'numeric'
        });
      }

      return this.dateFormatter.format(date);
    }
  }

  customElements.define('weekly-preorder-countdown', WeeklyPreorderCountdown);
}
